// ---------------------------------------------------------------------------
// Temas de EVENTO — o tema grátis que só existe dentro de uma janela.
//
// Mock aprovado 07/10/2026: `redesign-livra/tema-halloween/` (decisão ④ A:
// "grátis para todos, só na janela; fechou, quem estava com ele volta ao tema
// que usava antes"). Blueprint: `docs/systems/seasonal-themes.md` §11.
//
// ## 🔴 Isto é o OPOSTO de `themeSeasons.ts`, e é por isso que mora à parte
//
// Na temporada (Lua, Sol) a janela é de VITRINE: quem comprou fica com o tema
// para sempre, e o resolvedor é proibido de conhecer a data — há teste
// estrutural travando o import. Aqui a janela é o DIREITO: o tema não é
// comprado, é emprestado a todo mundo durante o evento, e por isso
// `resolveEquippedThemeId` TEM de consultar a data.
//
// Misturar os dois num módulo só convidaria o próximo a "também checar a
// janela" de um tema pago — que é o dia em que todo comprador da Lua perde o
// tema em silêncio. Dois arquivos, duas regras, e o nome de cada função diz
// qual é.
//
// Só tema SEM PREÇO pode ser de evento (`themeCatalogSync` cobra): tirar da
// tela um tema que alguém pagou é o BUG-80 com item pago.
//
// ## A comparação é de CHAVE DE DIA, como na temporada
//
// `dayKeyForBR` no fuso do produto; string "AAAA-MM-DD" ordena igual à data.
// `Date` traria o fuso do aparelho para a decisão de direito, e dono e
// visitante em fusos diferentes veriam temas diferentes na virada.
// ---------------------------------------------------------------------------

import { dayKeyForBR } from "../lib/time";

export interface ThemeEvent {
  /** Estável entre edições: o de 2027 reusa este id. */
  id: string;
  label: string;
  /** Primeiro dia, chave BR, **inclusivo**. */
  firstDay: string;
  /** Último dia, chave BR, **inclusivo** — o tema sai na virada, 00:00 BRT. */
  lastDay: string;
  edition: string;
}

/**
 * Os eventos conhecidos.
 *
 * ⚠️ Espelhado em `functions/src/shared/equippedTheme.ts` (`THEME_EVENTS`): o
 * cartão de "Leitores parecidos" pinta o tema que o SERVIDOR resolve. Data
 * diferente nas duas pontas = a pessoa sai do Halloween no app dela e continua
 * de abóbora na Início dos outros. `equippedThemeParity.test.ts` cobra.
 *
 * Quais temas pertencem ao evento é declarado no `themeRegistry` (campo
 * `event`), numa direção só — mesma regra da temporada.
 */
export const THEME_EVENTS: readonly ThemeEvent[] = [
  {
    id: "halloween",
    label: "Halloween",
    // Era 24/10 (limite para a 1.12 estar nas lojas, decisão ⑤ do mock).
    // Antecipado para 15/10 pelo fundador: a 1.12 está nas lojas desde 10/10,
    // mas o app da loja traz 24/10 embarcado — só a web abre antes.
    // 02/11 é Finados, o fim natural da semana do Halloween.
    firstDay: "2026-10-15",
    lastDay: "2026-11-02",
    edition: "2026",
  },
];

export function getThemeEvent(eventId: string | null | undefined): ThemeEvent | null {
  if (!eventId) return null;
  return THEME_EVENTS.find((e) => e.id === eventId) ?? null;
}

/**
 * O evento está no ar — ou seja, o tema dele pode ser USADO agora?
 *
 * Evento DESCONHECIDO devolve `false` (falha fechada): um typo tira o tema de
 * todo mundo, que é visível e corrigível, em vez de deixá-lo para sempre.
 */
export function isThemeEventOpen(eventId: string | null | undefined, at: Date = new Date()): boolean {
  const ev = getThemeEvent(eventId);
  if (!ev) return false;
  const today = dayKeyForBR(at);
  return today >= ev.firstDay && today <= ev.lastDay;
}

/** Dias que restam, contando hoje (1 no último dia), ou `null` fora do evento. */
export function themeEventDaysRemaining(
  eventId: string | null | undefined,
  at: Date = new Date(),
): number | null {
  const ev = getThemeEvent(eventId);
  if (!ev || !isThemeEventOpen(eventId, at)) return null;
  const today = Date.parse(`${dayKeyForBR(at)}T00:00:00-03:00`);
  const last = Date.parse(`${ev.lastDay}T00:00:00-03:00`);
  return Math.floor((last - today) / 86_400_000) + 1;
}

/** "02/11" — o último dia, para a copy "grátis até …". */
export function themeEventLastDayLabel(eventId: string | null | undefined): string | null {
  const ev = getThemeEvent(eventId);
  if (!ev) return null;
  const [, m, d] = ev.lastDay.split("-");
  return `${d}/${m}`;
}
